
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SystemUpdateAltRoundedIcon from "@mui/icons-material/SystemUpdateAltRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import InstallMobileRoundedIcon from "@mui/icons-material/InstallMobileRounded";
import { Box, ButtonBase, Portal, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

import { useAppStatus } from "../status/useAppStatus";
import StatusDetailsSheet from "./StatusDetailsSheet";
import { useAppUpdate } from "../hooks/useAppUpdate";
import { APP_OVERLAY } from "../ui/overlayLayers";
import {
  hasShownCompletedMaterialentnahmeAppUpdateInIsland,
  markCompletedMaterialentnahmeAppUpdateShownInIsland,
} from "../services/serviceWorkerService";

const CRITICAL_SERVICE_IDS = new Set(["internetConnection", "backendApi", "database", "emailWorker", "smtp", "emailRecipient", "fileStorage"]);
const DATA_SERVICE_IDS = new Set(["masterData", "allocationData", "ownershipData", "visionProduction"]);
function isDataUpdateService(service: { id:string; state:string; detail:string }): boolean {
  return DATA_SERVICE_IDS.has(service.id) && service.state === "warning"
    && (service.detail.includes("Neue Version verfügbar") || service.detail.includes("Neuer Stand verfügbar") || service.detail.includes("Aktualisierung"));
}

export default function AppStatusIsland() {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const { available: appUpdateAvailable } = useAppUpdate();
  const {
    activity,
    issues,
    services,
    queueEntries,
    clearIssue,
    clearAllIssues,
  } = useAppStatus();

  const failedQueueCount = queueEntries.filter((entry) => entry.state === "failed").length;
  const pendingQueueCount = queueEntries.filter(
    (entry) => entry.state === "pending" || entry.state === "retrying"
  ).length;

  const criticalServices = useMemo(() => services.filter((service) => CRITICAL_SERVICE_IDS.has(service.id)), [services]);
  const dataServices = useMemo(() => services.filter((service) => DATA_SERVICE_IDS.has(service.id)), [services]);
  const criticalServiceError = criticalServices.find((service) => service.state === "error") ?? null;
  const criticalServiceWarning = criticalServices.find((service) => service.state === "warning") ?? null;
  const dataError = dataServices.find((service) => service.state === "error") ?? null;
  const dataWarning = dataServices.find((service) => service.state === "warning" && !isDataUpdateService(service)) ?? null;
  const dataUpdates = dataServices.filter(isDataUpdateService);

  const completedAppUpdateIssue = useMemo(
    () => issues.find((issue) => issue.id.startsWith("app-updated:")) ?? null,
    [issues],
  );
  const [activeCompletedAppUpdateVersion, setActiveCompletedAppUpdateVersion] = useState<string | null>(null);

  useEffect(() => {
    if (!completedAppUpdateIssue) return;
    const version = completedAppUpdateIssue.id.slice("app-updated:".length);
    if (!version || hasShownCompletedMaterialentnahmeAppUpdateInIsland(version)) return;
    markCompletedMaterialentnahmeAppUpdateShownInIsland(version);
    const showTimer = window.setTimeout(() => setActiveCompletedAppUpdateVersion(version), 0);
    const hideTimer = window.setTimeout(() => {
      setActiveCompletedAppUpdateVersion((current) => current === version ? null : current);
    }, 5000);
    return () => { window.clearTimeout(showTimer); window.clearTimeout(hideTimer); };
  }, [completedAppUpdateIssue]);

  const completedAppUpdateVersion = completedAppUpdateIssue?.id.slice("app-updated:".length) ?? null;
  const showCompletedAppUpdate = Boolean(completedAppUpdateVersion && activeCompletedAppUpdateVersion === completedAppUpdateVersion);

  const priorityIssues = useMemo(() => issues.filter((issue) => {
    if (issue.title === "Neue Materialstammliste verfügbar") return false;
    if (issue.title === "Neue Kontierungsliste verfügbar") return false;
    if (issue.title === "Neue Ordnungspatenschaften verfügbar") return false;
    if (issue.id.startsWith("app-update:")) return false;
    if (showCompletedAppUpdate && issue.id.startsWith("app-updated:")) return false;
    return true;
  }), [issues, showCompletedAppUpdate]);
  const errorIssueCount = priorityIssues.filter((issue) => issue.severity === "error").length;
  const warningIssueCount = priorityIssues.filter((issue) => issue.severity === "warning").length;

  const serviceDisplayName = (id: string, fallback: string): string => {
    if (id === "internetConnection") return "Internet";
    if (id === "backendApi") return "Server";
    if (id === "emailWorker") return "E-Mail-Versand";
    if (id === "smtp") return "E-Mail-Verbindung";
    if (id === "fileStorage") return "Dateiablage";
    return fallback;
  };
  const serviceLabel = (service: typeof criticalServiceError) => service
    ? service.id === "backendApi" && service.state === "error" ? "Server nicht erreichbar"
      : service.state === "error" ? `${serviceDisplayName(service.id, service.label)} nicht verfügbar`
        : `${serviceDisplayName(service.id, service.label)} eingeschränkt`
    : null;
  const dataErrorLabel = dataError ? `${dataError.label} nicht verfügbar` : null;
  const dataWarningLabel = dataWarning?.id === "visionProduction" ? "Erkennung: Fallback aktiv" : dataWarning ? `${dataWarning.label} eingeschränkt` : null;
  const singleUpdateLabel = dataUpdates.length === 1
    ? dataUpdates[0].id === "masterData" ? "Materialstamm: Update"
      : dataUpdates[0].id === "allocationData" ? "Kontierungen: Update"
        : dataUpdates[0].id === "ownershipData" ? "Ordnungspatenschaften: Update"
          : "Erkennung: Update"
    : null;

  type DisplayKind = "serviceError" | "queueFailed" | "dataError" | "issueError" | "serviceWarning" | "queuePending" | "dataWarning" | "issueWarning" | "appUpdated" | "appUpdate" | "dataUpdate" | "issueInfo" | "activity" | "ready";
  const displayKind: DisplayKind = criticalServiceError ? "serviceError"
    : failedQueueCount > 0 ? "queueFailed"
    : dataError ? "dataError"
    : errorIssueCount > 0 ? "issueError"
    : criticalServiceWarning ? "serviceWarning"
    : pendingQueueCount > 0 ? "queuePending"
    : dataWarning ? "dataWarning"
    : warningIssueCount > 0 ? "issueWarning"
    : showCompletedAppUpdate ? "appUpdated"
    : appUpdateAvailable ? "appUpdate"
    : dataUpdates.length > 0 ? "dataUpdate"
    : priorityIssues.length > 0 ? "issueInfo"
    : activity ? "activity"
    : "ready";

  const label = displayKind === "serviceError" ? serviceLabel(criticalServiceError)
    : displayKind === "queueFailed" ? `${failedQueueCount} fehlgeschlagen`
    : displayKind === "dataError" ? dataErrorLabel
    : displayKind === "issueError" ? `${errorIssueCount} ${errorIssueCount === 1 ? "Fehler" : "Fehler"}`
    : displayKind === "serviceWarning" ? serviceLabel(criticalServiceWarning)
    : displayKind === "queuePending" ? `${pendingQueueCount} ausstehend`
    : displayKind === "dataWarning" ? dataWarningLabel
    : displayKind === "issueWarning" ? `${warningIssueCount} ${warningIssueCount === 1 ? "Warnung" : "Warnungen"}`
    : displayKind === "appUpdated" ? "App aktualisiert"
    : displayKind === "appUpdate" ? "App-Update verfügbar"
    : displayKind === "dataUpdate" ? (dataUpdates.length > 1 ? `${dataUpdates.length} Updates verfügbar` : singleUpdateLabel)
    : displayKind === "issueInfo" ? `${priorityIssues.length} ${priorityIssues.length === 1 ? "Meldung" : "Meldungen"}`
    : displayKind === "activity" ? activity
    : "System bereit";

  const displaySeverity = ["serviceError", "queueFailed", "dataError", "issueError"].includes(displayKind) ? "error"
    : ["serviceWarning", "queuePending", "dataWarning", "issueWarning"].includes(displayKind) ? "warning"
      : ["appUpdated", "appUpdate", "dataUpdate", "issueInfo"].includes(displayKind) ? "info"
        : "ok";
  const stateColor = displaySeverity === "error" ? "#ff4d55" : displaySeverity === "warning" ? "#f4a340" : displaySeverity === "info" ? "#83cee0" : "#43d17a";

  return (
    <>
      <Portal>
      <Box
        sx={{
          position: "fixed",
          zIndex: APP_OVERLAY.statusIsland,
          top: "calc(8px + env(safe-area-inset-top))",
          left: "50%",
          transform: "translateX(-50%)",
          pointerEvents: "none",
        }}
      >
        <ButtonBase
          onClick={() => setDetailsOpen((current) => !current)}
          aria-label="Systemstatus öffnen"
          sx={{
            pointerEvents: "auto",
            minWidth: 164,
            width: "fit-content",
            maxWidth: "calc(100vw - 34px)",
            height: 42,
            px: 1.35,
            borderRadius: 999,
            color: "#fff",
            background: "linear-gradient(180deg, rgba(6,9,11,.985) 0%, rgba(0,0,0,.985) 100%)",
            border: detailsOpen ? "1px solid rgba(78,224,249,.62)" : "1px solid rgba(255,255,255,.16)",
            boxShadow: detailsOpen
              ? "0 0 18px rgba(67,222,247,.42), 0 8px 24px rgba(0,0,0,.42), inset 0 1px 0 rgba(255,255,255,.08)"
              : "0 7px 24px rgba(0,0,0,.34), inset 0 1px 0 rgba(255,255,255,.06)",
            backdropFilter: "blur(18px) saturate(135%)",
            WebkitBackdropFilter: "blur(18px) saturate(135%)",
            overflow: "hidden",
          }}
        >
          <Box
            aria-hidden="true"
            sx={{
              width: 10,
              height: 10,
              ml: 0.1,
              mr: 0.9,
              flex: "0 0 auto",
              borderRadius: "50%",
              bgcolor: stateColor,
              boxShadow: `0 0 11px ${stateColor}, 0 0 24px ${stateColor}66`,
              position: "relative",
              "&::after": {
                content: '""',
                position: "absolute",
                inset: -2,
                borderRadius: "50%",
                border: `1px solid ${stateColor}`,
                animation: "islandStatusPulse 1.9s ease-out infinite",
              },
              "@keyframes islandStatusPulse": {
                "0%": { transform: "scale(.92)", opacity: 0.65 },
                "75%": { transform: "scale(1.75)", opacity: 0 },
                "100%": { transform: "scale(.92)", opacity: 0 },
              },
            }}
          />


          <Typography
            variant="body2"
            noWrap
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: "center",
              color: "rgba(255,255,255,.94)",
              fontWeight: 800,
              fontSize: "0.86rem",
              letterSpacing: ".005em",
            }}
          >
            {label}
          </Typography>

          {displaySeverity === "error" && (
            <ErrorOutlineRoundedIcon
              sx={{ ml: 0.9, fontSize: 20, color: "#ff737a", flex: "0 0 auto" }}
            />
          )}
          {displayKind === "appUpdate" && (
            <InstallMobileRoundedIcon sx={{ ml: 0.9, fontSize: 20, color: "#83cee0", flex: "0 0 auto" }} />
          )}
          {displayKind === "appUpdated" && (
            <CheckCircleOutlineRoundedIcon sx={{ ml: 0.9, fontSize: 20, color: "#83cee0", flex: "0 0 auto" }} />
          )}
          {displayKind === "dataUpdate" && (
            <SystemUpdateAltRoundedIcon sx={{ ml: 0.9, fontSize: 20, color: "#83cee0", flex: "0 0 auto" }} />
          )}
          {displayKind !== "appUpdate" && displayKind !== "appUpdated" && displayKind !== "dataUpdate" && displaySeverity === "warning" && (
            <WarningAmberRoundedIcon sx={{ ml: 0.9, fontSize: 19, color: "#f4a340", flex: "0 0 auto" }} />
          )}

          <KeyboardArrowDownRoundedIcon
            sx={{
              ml: 0.55,
              mr: -0.35,
              fontSize: 20,
              color: "rgba(255,255,255,.76)",
              transform: detailsOpen ? "rotate(180deg)" : "none",
              transition: "transform 160ms ease",
              flex: "0 0 auto",
            }}
          />
        </ButtonBase>
      </Box>
      </Portal>

      <StatusDetailsSheet
        open={detailsOpen}
        issues={issues}
        services={services}
        queueEntries={queueEntries}
        onClose={() => setDetailsOpen(false)}
        onClearIssue={clearIssue}
        onClearAll={clearAllIssues}
        onOpenHistoryEntry={({ withdrawalId, clientUuid }) => {
          setDetailsOpen(false);
          window.dispatchEvent(new CustomEvent("materialentnahme:open-history-entry", { detail: { withdrawalId, clientUuid } }));
        }}
      />
    </>
  );
}
